import type { KeyboardEvent } from 'react'
import { ChevronRight, Star } from 'lucide-react'
import type { SupportedLocale } from '../../lib/i18n'
import type { AdminProviderSummary } from '../../graphql/admin'
import ProviderStatusBadge from './ProviderStatusBadge'
import { formatAdminDate, formatAdminUserName } from './formatters'

interface ProviderTableProps {
  providers: AdminProviderSummary[]
  locale: SupportedLocale
  emptyMessage: string
  onSelectProvider: (providerId: string) => void
}

export default function ProviderTable({ providers, locale, emptyMessage, onSelectProvider }: ProviderTableProps) {
  function handleKeyDown(event: KeyboardEvent<HTMLTableRowElement>, providerId: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelectProvider(providerId)
    }
  }

  if (providers.length === 0) {
    return (
      <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-500">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-[1.5rem] border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          <tr>
            <th className="px-5 py-4">Name</th>
            <th className="px-5 py-4">Email</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4">Rating</th>
            <th className="px-5 py-4">Jobs</th>
            <th className="px-5 py-4">Joined</th>
            <th className="px-5 py-4" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {providers.map((provider) => (
            <tr
              key={provider.id}
              tabIndex={0}
              role="link"
              onClick={() => onSelectProvider(provider.id)}
              onKeyDown={(event) => handleKeyDown(event, provider.id)}
              className="cursor-pointer transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            >
              <td className="px-5 py-4">
                <div className="font-semibold text-slate-950">{formatAdminUserName(provider.user)}</div>
                {provider.onVacation ? (
                  <div className="mt-1 text-xs font-medium text-amber-700">Vacation mode active</div>
                ) : null}
              </td>
              <td className="px-5 py-4 text-sm text-slate-600">{provider.user.email}</td>
              <td className="px-5 py-4">
                <ProviderStatusBadge status={provider.verificationStatus} />
              </td>
              <td className="px-5 py-4 text-sm text-slate-700">
                <span className="inline-flex items-center gap-1 font-semibold text-slate-900">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  {provider.averageRating.toFixed(1)}
                </span>
                <div className="mt-1 text-xs text-slate-500">{provider.totalReviews} reviews</div>
              </td>
              <td className="px-5 py-4 text-sm font-semibold text-slate-900">{provider.completedJobs}</td>
              <td className="px-5 py-4 text-sm text-slate-600">{formatAdminDate(provider.createdAt, locale)}</td>
              <td className="px-5 py-4 text-right text-slate-400">
                <ChevronRight className="ml-auto h-4 w-4" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
